import { Injectable } from '@angular/core';
import { FABAction, FABActionType } from '../models/fab-action.interface';
import { FABActionDefinitionsService } from './fab-action-definitions.service';

export interface FormObject {
	id?: string;
	status?: string;
	createdBy?: string;
	title?: string;
	departmentIds?: any[];
	committeIds?: any[];
	inchargeUserIds?: any[];
	involvedUserIds?: any[];
	department?: any[];
	committee?: any[];
	inchargeUser?: any[];
	involvedUser?: any[];
	rejectionMessage?: string;
	// IQAC subsection support
	formTypeDTO?: {
		fomSubSection?: {
			id?: number;
		};
	};
}

export interface UserRoles {
	isIQACADMIN?: boolean;
	isWEBEQUI?: boolean;
	isOFFICE_CO?: boolean;
	isTEACHER?: boolean;
	isLibrarian?: boolean;
	isCriteriaCoordinator?: boolean;
	isDuelRole?: boolean;
}

export interface DashboardContext {
	isFROMIQACDASHBOARD?: boolean;
	isFROM_CRITERIA_DASHBOARD?: boolean;
	isFROMPERSONALDASHBOARD?: boolean;
}

export interface FABContext {
	formObject: FormObject;
	userRoles: UserRoles;
	dashboardContext: DashboardContext;
	loggedInUserName: string;
	webCoCanSubmit?: boolean;
	subscriptionStatus?: boolean;
}

@Injectable({
	providedIn: 'root'
})
export class FABIntegrationService {
	constructor(private actionDefinitions: FABActionDefinitionsService) {}

	/**
	 * Generate FAB actions based on the current context
	 */
	generateFABActions(context: FABContext): FABAction[] {
		const actions: FABAction[] = [];

		console.log(`Form Id: ${context.formObject.id}`);

		// Primary actions (most important)
		const canSubmit = this.canSubmit(context);
		const canUpdate = this.canUpdate(context);
		const canApprove = this.canApprove(context);
		const canCreate = this.canCreate(context);

		if (canCreate) {
			actions.push(this.createAction('CREATE', context));
			if (!context.formObject.id) {
				return actions;
			}
		}
		if (canSubmit) {
			actions.push(this.createAction('SUBMIT', context));
		}
		if (canUpdate) {
			const updateAction = this.createAction('UPDATE', context);
			// If SUBMIT is also available, make UPDATE secondary (Save Draft)
			if (canSubmit) {
				updateAction.label = 'Update';
				updateAction.priority = 'secondary';
			}
			actions.push(updateAction);
		}
		if (canApprove) {
			actions.push(this.createAction('APPROVE', context));
		}

		// Secondary actions
		const canShare = this.canShare(context);
		const canForward = this.canForward(context);
		const canRecall = this.canRecall(context);
		const canResend = this.canResend(context);

		if (canShare) {
			actions.push(this.createAction('SHARE', context));
		}
		if (canForward) {
			actions.push(this.createAction('FORWARD', context));
		}
		if (canRecall) {
			actions.push(this.createAction('RECALL', context));
		}
		if (canResend) {
			actions.push(this.createAction('RESEND', context));
		}

		// For new forms, add UPDATE as a secondary action (Save Draft)
		// if (canCreate && !canUpdate) {
		// 	const saveDraftAction = this.createAction('UPDATE', context);
		// 	saveDraftAction.label = 'Save Draft';
		// 	saveDraftAction.priority = 'secondary';
		// 	actions.push(saveDraftAction);
		// }

		// Tertiary actions
		const canDelete = this.canDelete(context);
		const canRevert = this.canRevert(context);
		const canReplicate = this.canReplicate(context);

		if (canDelete) {
			actions.push(this.createAction('DELETE', context));
		}
		if (canRevert) {
			actions.push(this.createAction('REVERT', context));
		}
		if (canReplicate) {
			actions.push(this.createAction('REPLICATE', context));
		}

		return actions;
	}

	/**
	 * Generate dual role actions
	 */
	generateDualRoleFABActions(context: FABContext): FABAction[] {
		if (!context.userRoles.isDuelRole) {
			return this.generateFABActions(context);
		}

		// For dual role users, we need to consider both role permissions
		const actions: FABAction[] = [];

		// Primary actions
		if (this.canSubmitApartFromTeacher(context)) {
			actions.push(this.createAction('SUBMIT', context));
		}
		if (this.canUpdateApartFromTeacher(context)) {
			actions.push(this.createAction('UPDATE', context));
		}
		if (this.canApproveApartFromTeacher(context)) {
			actions.push(this.createAction('APPROVE', context));
		}
		if (this.canCreateApartFromTeacher(context)) {
			actions.push(this.createAction('CREATE', context));
		}

		// Secondary actions
		if (this.canForwardApartFromTeacher(context)) {
			actions.push(this.createAction('FORWARD', context));
		}
		if (this.canShareApartFromTeacher(context)) {
			actions.push(this.createAction('SHARE', context));
		}
		if (this.canRecallApartFromTeacher(context)) {
			actions.push(this.createAction('RECALL', context));
		}
		if (this.canResentApartFromTeacher(context)) {
			actions.push(this.createAction('RESEND', context));
		}
		if (this.canRevertApartFromTeacher(context)) {
			actions.push(this.createAction('REVERT', context));
		}
		// if (this.canReplicateApartFromTeacher(context)) {
		// 	actions.push(this.createAction('REPLICATE', context));
		// }

		// Tertiary actions
		if (this.canDelete(context)) {
			actions.push(this.createAction('DELETE', context));
		}

		return actions;
	}

	/**
	 * Create a FAB action based on type and context
	 */
	private createAction(type: FABActionType, context: FABContext): FABAction {
		const definition = this.actionDefinitions.getActionDefinition(type);
		const actionId = `${type}_${context.formObject.id || 'new'}`;
		let color = definition.color;
		if (type === 'REVERT' || type === 'DELETE') {
			color = 'danger';
		}
		return {
			id: actionId,
			label: definition.defaultLabel,
			icon: definition.defaultIcon,
			priority: definition.priority,
			color: color,
			visible: true,
			disabled: false,
			confirmationRequired: definition.requiresConfirmation,
			confirmationMessage: this.getConfirmationMessage(type),
			handler: this.createActionHandler(type, context)
		};
	}

	/**
	 * Get confirmation message for specific actions
	 */
	private getConfirmationMessage(type: FABActionType): string | undefined {
		switch (type) {
			case 'DELETE':
				return 'Do you want to delete this form? This action cannot be undone.';
			case 'RECALL':
				return 'Do you want to recall this form?';
			case 'REVERT':
				return 'Do you want to revert this form?';
			default:
				return undefined;
		}
	}

	/**
	 * Create action handler that returns a promise for the action
	 */
	private createActionHandler(type: FABActionType, context: FABContext): () => Promise<void> {
		return async () => {
			// This will be implemented in the parent component
			// The handler will emit the appropriate event
			console.log(`Action ${type} triggered for form ${context.formObject.id}`);
		};
	}

	// =================== SIMPLIFIED PERMISSION LOGIC ===================
	// These are simplified versions that avoid the Boolean logic errors

	canSubmit(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName, userRoles } = context;
		const formStatus = formObject.status;
		const submittableStatuses = ['CREATED', 'RECALLED', 'REVERTED', 'RESENT'];

		// Personal Dashboard submission (non-IQAC)
		const canPersonalSubmit =
			!dashboardContext.isFROMIQACDASHBOARD &&
			loggedInUserName === formObject.createdBy &&
			submittableStatuses.includes(formStatus || '');

		// IQAC Dashboard submission (IQAC_ADMIN only, subsection ID 31 only)
		const canIQACSubmit =
			dashboardContext.isFROMIQACDASHBOARD &&
			userRoles.isIQACADMIN &&
			this.isIQACSubsection(formObject) &&
			submittableStatuses.includes(formStatus || '');

		console.log(`canPersonalSubmit`, canPersonalSubmit);
		console.log(`canIQACSubmit`, canIQACSubmit);
		console.log(`IQAC Subsection ID`, formObject?.formTypeDTO?.fomSubSection?.id);

		return canPersonalSubmit || canIQACSubmit;
	}

	canUpdate(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName, userRoles } = context;
		const formStatus = formObject.status;
		const updatableStatuses = ['CREATED', 'REVERTED', 'RECALLED', 'RESENT'];
		const creator = formObject.createdBy;
		if (dashboardContext.isFROMIQACDASHBOARD) {
			// This is IQAC Section Form
			return formObject.id !== null && userRoles.isIQACADMIN && updatableStatuses.includes(formStatus || '');
		} else {
			// This is other section form
			return (
				(formObject.id !== null && updatableStatuses.includes(formStatus || '') && loggedInUserName === creator) ||
				(formStatus === 'SHARED' && loggedInUserName !== creator)
			);
		}
	}

	canApprove(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext } = context;
		const formStatus = formObject.status;
		const approvableStatuses = [
			'SUBMITTED_FOR_APPROVAL',
			'SHARED_WITH_WebC',
			'SHARED_WITH_OFFICE_CO',
			'SHARED_WITH_WEB_AND_OFFICE'
		];

		const canIQACAPPROVE =
			userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD && approvableStatuses.includes(formStatus || '');
		const canCRITERIA_CO_APPROVE =
			userRoles.isCriteriaCoordinator &&
			dashboardContext.isFROM_CRITERIA_DASHBOARD &&
			approvableStatuses.includes(formStatus || '');

		return canIQACAPPROVE || canCRITERIA_CO_APPROVE;
	}

	canCreate(context: FABContext): boolean {
		return !context.formObject.id;
	}

	canForward(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext } = context;
		const formStatus = formObject.status;
		const forwardableStatuses = ['APPROVED', 'APPROVED_WITH_WebC', 'APPROVED_WITH_OFFICE_CO'];

		return (
			userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD && forwardableStatuses.includes(formStatus || '')
		);
	}

	canShare(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName, userRoles } = context;
		const formStatus = formObject.status;
		const shareableStatuses = ['CREATED', 'RECALLED', 'REVERTED'];

		return (
			dashboardContext.isFROMPERSONALDASHBOARD &&
			formObject.id !== null &&
			loggedInUserName === formObject.createdBy &&
			shareableStatuses.includes(formStatus || '') &&
			!userRoles.isOFFICE_CO
		);
	}

	canRecall(context: FABContext): boolean {
		const { formObject, userRoles, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			!userRoles.isIQACADMIN &&
			formStatus === 'SHARED' &&
			formObject.id !== null &&
			loggedInUserName === formObject.createdBy
		);
	}

	canResend(context: FABContext): boolean {
		const { formObject, userRoles, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			!userRoles.isIQACADMIN &&
			formStatus === 'SHARED' &&
			formObject.id !== null &&
			loggedInUserName !== formObject.createdBy
		);
	}

	canDelete(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName } = context;
		const formStatus = formObject.status;
		const deletableStatuses = ['CREATED', 'RECALLED', 'REVERTED', 'RESENT'];

		return formObject.createdBy === loggedInUserName && deletableStatuses.includes(formStatus || '');
	}

	canReplicate(context: FABContext): boolean {
		// TODO later
		return false;
		// const { formObject, loggedInUserName } = context;
		// const formStatus = formObject.status;
		// const replicableStatuses = [
		// 	'APPROVED',
		// 	'APPROVED_WITH_WebC',
		// 	'APPROVED_WITH_OFFICE_CO',
		// 	'APPROVED_WITH_WEB_AND_OFFICE'
		// ];

		// return replicableStatuses.includes(formStatus || '') && loggedInUserName === formObject.createdBy;
	}

	canRevert(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext, loggedInUserName } = context;
		console.log(`In canRevert() - formObject.id - ${formObject.id}`);
		if (formObject.id === null) {
			return false;
		}
		const formStatus = formObject.status;
		if (userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD) {
			const revertableStatuses = [
				'SUBMITTED_FOR_APPROVAL',
				'APPROVED',
				'APPROVED_WITH_WebC',
				'APPROVED_WITH_OFFICE_CO',
				'APPROVED_WITH_WebC_UPDATED',
				'APPROVED_WITH_OFFICE_CO_UPDATED',
				'APPROVED_WITH_WEB_AND_OFFICE_UPDATED',
				'SHARED_WITH_WEB_AND_OFFICE',
				'SHARED_WITH_WebC',
				'SHARED_WITH_OFFICE_CO',
				'APPROVED_WITH_BOTH_WebC_UPDATED',
				'APPROVED_WITH_BOTH_OFFICE_UPDATED',
				'APPROVED_WITH_WEB_AND_OFFICE'
			];
			return revertableStatuses.includes(formStatus || '');
		}
		let canIQAC_REVERT = userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD && formStatus != 'REVERTED';
		let canCRITERIA_CO_REVERT =
			userRoles.isCriteriaCoordinator && dashboardContext.isFROM_CRITERIA_DASHBOARD && formStatus != 'REVERTED';
		return canIQAC_REVERT || canCRITERIA_CO_REVERT;
	}

	/**
	 * Check if form is an IQAC subsection form (ID 31)
	 * Used for IQAC-specific submission rules
	 */
	private isIQACSubsection(formObject: FormObject): boolean {
		return formObject?.formTypeDTO?.fomSubSection?.id !== undefined && formObject.formTypeDTO.fomSubSection.id === 31;
	}

	// =================== DUAL ROLE PERMISSION METHODS ===================

	private canSubmitApartFromTeacher(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			loggedInUserName === formObject.createdBy &&
			!dashboardContext.isFROMIQACDASHBOARD &&
			(formStatus === 'CREATED' || formStatus === 'RECALLED' || formStatus === 'REVERTED' || formStatus === 'RESENT')
		);
	}

	private canUpdateApartFromTeacher(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			(formObject.id &&
				!dashboardContext.isFROMIQACDASHBOARD &&
				(formStatus === 'CREATED' ||
					(formStatus === 'REVERTED' && loggedInUserName === formObject.createdBy) ||
					(formStatus === 'RECALLED' && loggedInUserName === formObject.createdBy))) ||
			(formStatus === 'RESENT' && loggedInUserName === formObject.createdBy) ||
			(formStatus === 'SHARED' && loggedInUserName !== formObject.createdBy)
		);
	}

	// private canReplicateApartFromTeacher(context: FABContext): boolean {
	// 	const { formObject, loggedInUserName } = context;
	// 	const formStatus = formObject.status;

	// 	const statuscheck =
	// 		formStatus === 'APPROVED' ||
	// 		formStatus === 'APPROVED_WITH_WebC' ||
	// 		formStatus === 'APPROVED_WITH_OFFICE_CO' ||
	// 		formStatus === 'APPROVED_WITH_WEB_AND_OFFICE' ||
	// 		formStatus === 'APPROVED_WITH_WebC_UPDATED' ||
	// 		formStatus === 'APPROVED_WITH_OFFICE_CO_UPDATED' ||
	// 		formStatus === 'APPROVED_WITH_WEB_AND_OFFICE_UPDATED' ||
	// 		formStatus === 'APPROVED_WITH_BOTH_WebC_UPDATED' ||
	// 		formStatus === 'APPROVED_WITH_BOTH_OFFICE_UPDATED';

	// 	return statuscheck && loggedInUserName === formObject.createdBy;
	// }

	private canCreateApartFromTeacher(context: FABContext): boolean {
		return context.formObject.id == null;
	}

	private canShareApartFromTeacher(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName, userRoles } = context;
		const formStatus = formObject.status;

		return (
			formStatus !== 'APPROVED' &&
			formStatus !== 'SHARED' &&
			formStatus !== 'SUBMITTED_FOR_APPROVAL' &&
			formObject.id !== null &&
			formStatus !== 'APPROVED_WITH_WebC' &&
			formStatus !== 'APPROVED_WITH_OFFICE_CO' &&
			formStatus !== 'APPROVED_WITH_WEB_AND_OFFICE' &&
			formStatus !== 'SHARED_WITH_OFFICE_CO' &&
			formStatus !== 'SHARED_WITH_WebC' &&
			formStatus !== 'SHARED_WITH_WEB_AND_OFFICE' &&
			formStatus !== 'APPROVED_WITH_WebC_UPDATED' &&
			formStatus !== 'APPROVED_WITH_OFFICE_CO_UPDATED' &&
			formStatus !== 'APPROVED_WITH_WEB_AND_OFFICE_UPDATED' &&
			formStatus !== 'APPROVED_WITH_BOTH_WebC_UPDATED' &&
			formStatus !== 'APPROVED_WITH_BOTH_OFFICE_UPDATED' &&
			loggedInUserName === formObject.createdBy &&
			dashboardContext.isFROMPERSONALDASHBOARD &&
			!userRoles.isOFFICE_CO
		);
	}

	private canRecallApartFromTeacher(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			formStatus === 'SHARED' &&
			!dashboardContext.isFROMIQACDASHBOARD &&
			formObject.id !== null &&
			loggedInUserName === formObject.createdBy
		);
	}

	private canResentApartFromTeacher(context: FABContext): boolean {
		const { formObject, dashboardContext, loggedInUserName } = context;
		const formStatus = formObject.status;

		return (
			formStatus === 'SHARED' &&
			!dashboardContext.isFROMIQACDASHBOARD &&
			formObject.id !== null &&
			loggedInUserName !== formObject.createdBy
		);
	}

	private canRevertApartFromTeacher(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext } = context;
		const formStatus = formObject.status;

		const formStatusForReverted =
			formStatus === 'SUBMITTED_FOR_APPROVAL' ||
			formStatus === 'APPROVED' ||
			formStatus === 'APPROVED_WITH_WebC' ||
			formStatus === 'APPROVED_WITH_OFFICE_CO' ||
			formStatus === 'APPROVED_WITH_WebC_UPDATED' ||
			formStatus === 'APPROVED_WITH_OFFICE_CO_UPDATED' ||
			formStatus === 'APPROVED_WITH_WEB_AND_OFFICE_UPDATED' ||
			formStatus === 'SHARED_WITH_WEB_AND_OFFICE' ||
			formStatus === 'SHARED_WITH_WebC' ||
			formStatus === 'SHARED_WITH_OFFICE_CO' ||
			formStatus === 'APPROVED_WITH_BOTH_WebC_UPDATED' ||
			formStatus === 'APPROVED_WITH_BOTH_OFFICE_UPDATED' ||
			formStatus === 'APPROVED_WITH_WEB_AND_OFFICE';

		const canIQAC_REVERT = userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD && formStatusForReverted;
		const canCRITERIA_CO_REVERT =
			userRoles.isCriteriaCoordinator && dashboardContext.isFROM_CRITERIA_DASHBOARD && formStatusForReverted;

		return canIQAC_REVERT || canCRITERIA_CO_REVERT;
	}

	private canApproveApartFromTeacher(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext } = context;
		const formStatus = formObject.status;

		// Only these statuses can be approved
		const approvableStatuses = [
			'SUBMITTED_FOR_APPROVAL',
			'SHARED_WITH_WEB_AND_OFFICE',
			'SHARED_WITH_WebC',
			'SHARED_WITH_OFFICE_CO'
		];

		const formStatusForApproval = approvableStatuses.includes(formStatus || '');

		const canIQACAPPROVE = userRoles.isIQACADMIN && dashboardContext.isFROMIQACDASHBOARD && formStatusForApproval;
		const canCRITERIA_CO_APPROVE =
			userRoles.isCriteriaCoordinator && dashboardContext.isFROM_CRITERIA_DASHBOARD && formStatusForApproval;

		return canIQACAPPROVE || canCRITERIA_CO_APPROVE;
	}

	private canForwardApartFromTeacher(context: FABContext): boolean {
		const { formObject, userRoles, dashboardContext } = context;
		const formStatus = formObject.status;

		// Statuses that can be forwarded
		const forwardableStatuses = [
			'SHARED_WITH_WebC',
			'SHARED_WITH_OFFICE_CO',
			'APPROVED',
			'APPROVED_WITH_WebC',
			'APPROVED_WITH_WebC_UPDATED',
			'APPROVED_WITH_OFFICE_CO',
			'APPROVED_WITH_OFFICE_CO_UPDATED'
		];

		// Statuses that cannot be forwarded (exclusions)
		const nonForwardableStatuses = [
			'SHARED_WITH_WEB_AND_OFFICE',
			'APPROVED_WITH_WEB_AND_OFFICE',
			'APPROVED_WITH_WEB_AND_OFFICE_UPDATED',
			'REVERTED',
			'SUBMITTED_FOR_APPROVAL',
			'APPROVED_WITH_BOTH_WebC_UPDATED',
			'APPROVED_WITH_BOTH_OFFICE_UPDATED'
		];

		return (
			userRoles.isIQACADMIN &&
			dashboardContext.isFROMIQACDASHBOARD &&
			forwardableStatuses.includes(formStatus || '') &&
			!nonForwardableStatuses.includes(formStatus || '')
		);
	}
}
